import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { ArrowLeft, FloppyDisk } from "@phosphor-icons/react";

export default function PatientNew() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    fiscal_code: "",
    phone: "",
    email: "",
    dob: "",
    intervention_date: "",
    intervention_type: "",
    intervention_details: "",
    post_op_instructions: "",
    materials_used: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/patients", { ...form, implants: [], medications: [] });
      navigate(`/patients/${data.id}`, { state: { justCreated: true, pin: data.access_pin } });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl fade-up" data-testid="patient-new-page">
      <Link to="/patients" className="inline-flex items-center gap-2 text-sm text-brand-700 hover:text-brand-800 mb-6">
        <ArrowLeft size={16} weight="bold" /> Torna ai pazienti
      </Link>
      <div className="text-xs uppercase tracking-[0.25em] text-brand-400">Nuovo paziente</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-[#1C2321] mt-2">Crea scheda paziente</h1>
      <p className="text-[#56635D] mt-2">Inserisci i dati essenziali. Potrai aggiungere impianti, materiali e referti dopo la creazione.</p>

      <form onSubmit={submit} className="mt-8 space-y-6">
        <Section title="Anagrafica">
          <Field label="Nome" required>
            <input data-testid="patient-first-name" required value={form.first_name} onChange={change("first_name")} className="input" />
          </Field>
          <Field label="Cognome" required>
            <input data-testid="patient-last-name" required value={form.last_name} onChange={change("last_name")} className="input" />
          </Field>
          <Field label="Codice fiscale">
            <input data-testid="patient-fiscal-code" value={form.fiscal_code} onChange={change("fiscal_code")} className="input uppercase" />
          </Field>
          <Field label="Data di nascita">
            <input data-testid="patient-dob" type="date" value={form.dob} onChange={change("dob")} className="input" />
          </Field>
          <Field label="Telefono">
            <input data-testid="patient-phone" value={form.phone} onChange={change("phone")} placeholder="+39 …" className="input" />
          </Field>
          <Field label="Email">
            <input data-testid="patient-email" type="email" value={form.email} onChange={change("email")} className="input" />
          </Field>
        </Section>

        <Section title="Intervento">
          <Field label="Data intervento">
            <input data-testid="patient-intervention-date" type="date" value={form.intervention_date} onChange={change("intervention_date")} className="input" />
          </Field>
          <Field label="Tipo intervento">
            <input data-testid="patient-intervention-type" value={form.intervention_type} onChange={change("intervention_type")} placeholder="Es. Implantologia 1.6" className="input" />
          </Field>
          <Field label="Dettagli intervento" full>
            <textarea data-testid="patient-intervention-details" rows={3} value={form.intervention_details} onChange={change("intervention_details")} className="input" />
          </Field>
          <Field label="Materiali utilizzati" full>
            <textarea data-testid="patient-materials" rows={2} value={form.materials_used} onChange={change("materials_used")} className="input" />
          </Field>
          <Field label="Istruzioni post-operatorie" full>
            <textarea data-testid="patient-post-op" rows={3} value={form.post_op_instructions} onChange={change("post_op_instructions")} className="input" />
          </Field>
          <Field label="Note interne" full>
            <textarea data-testid="patient-notes" rows={2} value={form.notes} onChange={change("notes")} className="input" />
          </Field>
        </Section>

        {error && <div className="text-sm text-[#A84A4A] bg-[#A84A4A]/10 rounded-lg px-3 py-2">{error}</div>}

        <div className="flex gap-3">
          <button type="button" onClick={() => navigate(-1)} className="px-5 py-3 rounded-lg bg-[#EAF0EC] text-brand-700 hover:bg-[#D5E2DB]">
            Annulla
          </button>
          <button
            type="submit"
            disabled={submitting}
            data-testid="patient-save-button"
            className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-5 py-3 rounded-lg disabled:opacity-60"
          >
            <FloppyDisk size={18} weight="bold" /> {submitting ? "Salvataggio…" : "Crea paziente"}
          </button>
        </div>
      </form>

      <style>{`.input{width:100%;padding:.7rem .9rem;border:1px solid #E6E8E6;border-radius:.5rem;background:#fff;transition:border-color .2s} .input:focus{border-color:#2C4A3B;outline:none}`}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="elev-card p-6">
      <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-4">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, children, required, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="block text-xs uppercase tracking-[0.18em] text-[#56635D] mb-1.5">
        {label} {required && <span className="text-[#A84A4A]">*</span>}
      </label>
      {children}
    </div>
  );
}
